import { useLocation } from '@solidjs/router';
import { gql } from 'graphql-request';
import { createSignal, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { fieldShellClasses, FormFieldLabel } from '../forms-tw/forms-fields/primitives';
import { cviSemantic } from '../../style/theme';

export const resetPasswordMutation = gql`
    mutation resetPassword($email: String!, $password: String!, $token: String!) {
        resetPassword(
            email: $email
            password: $password
            token: $token
        ) {
            status
            message
        }
    }
`;

export const careResetPasswordMutation = gql`
    mutation careResetPassword(
        $email: String!
        $password: String!
        $token: String!
        $site: String!
    ) {
        careResetPassword(
            email: $email
            password: $password
            token: $token
            site: $site
        ) {
            status
            message
        }
    }
`;


export const PasswordReset = () => {
    const { graphQlClient, localize, siteInfo } = useContext(AppContext);
    const [password,setPassword] = createSignal('');
    const [verifyPassword,setVerifyPassword] = createSignal('');
    const [error,setError] = createSignal('');
    const [message, setMessage] = createSignal('');
    const [loading, setLoading] = createSignal(false);
    const { token, email: encodedEmail } = useLocation().query;
    let passwordInputRef: HTMLInputElement | undefined;

    onMount(() => passwordInputRef?.focus());

    const submitResetPasswordForm = async () => {
        if (password() !== verifyPassword()) {
            setError(localize('passwords-do-not-match', 'Passwords do not match'));
            return;
        }

        if (password() === '') {
            setError(localize('password-is-required', 'Password is required'));
            return;
        }

        const isCareSite = siteInfo.siteType === 'atos-care';        
        const query = isCareSite ? careResetPasswordMutation : resetPasswordMutation;

        const email = decodeURIComponent(encodedEmail).split('?')[0];

        const vars = {
            email,
            token,
            password: password(),
            site: siteInfo.siteSlug,
        };

        setLoading(true);

        try {
            const response = await graphQlClient(query, vars, false);
            const { status, message } = isCareSite ? response.careResetPassword : response.resetPassword;
    
            if(status !== 'OK') {
                setError(message);
            } else {
                setMessage(message);
            }
        } catch (e) {
            setMessage(localize('error-has-occurred', 'An error has occurred'));
            console.log('error', e);
        }

        setLoading(false);
    };

    return (
        <Section widthType="bgFull" templateShorthand={[12]}>
            <div class="mx-auto w-full max-w-cvi-form-column">
                <Heading tag='h1'>{localize('reset-password', 'Reset password')}</Heading>

                <Show when={error()}>
                    <div class="mb-cvi-8 body-sm font-cvi-regular text-cvi-fg-danger">
                        {error()}
                    </div>
                </Show>

                <Show when={message()}>
                    <div class="body-sm font-cvi-regular text-cvi-fg-default">
                        {localize(message(), 'Your password was successfully changed')}
                    </div>
                </Show>

                <Show when={!message()}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitResetPasswordForm();
                        }}
                    >
                        <div class="flex flex-col gap-cvi-7">
                            <div>
                                <FormFieldLabel for="new-password">
                                    {localize('new-password', 'New password')}
                                </FormFieldLabel>

                                <input
                                    ref={passwordInputRef}
                                    id="new-password"
                                    value={password()}
                                    onInput={(e) => {
                                        setError('');
                                        setPassword(e.currentTarget.value);
                                    }}
                                    type="password"
                                    name="new-password"
                                    autocomplete="new-password"
                                    class={fieldShellClasses(!!error())}
                                />
                            </div>

                            <div>
                                <FormFieldLabel for="verify-new-password">
                                    {localize('verify-new-password', 'Verify new password')}
                                </FormFieldLabel>

                                <input
                                    id="verify-new-password"
                                    value={verifyPassword()}
                                    onInput={(e) => {
                                        setError('');
                                        setVerifyPassword(e.currentTarget.value);
                                    }}
                                    type="password"
                                    name="verify-new-password"
                                    autocomplete="new-password"
                                    class={fieldShellClasses(!!error())}
                                />
                            </div>
                        </div>

                        <div class="mt-cvi-10">
                            <Button
                                label={localize('submit-password-reset-form-button-text', 'Reset')}
                                type="submit"
                                disabled={loading()}
                                noBlockSpacing={true}
                                customCss="width: 100%"
                            />
                        </div>

                        <Show when={loading()}>
                            <div class="mt-cvi-6 flex justify-center">
                                <LoadingSpinner color={cviSemantic.fgBrand} />
                            </div>
                        </Show>
                    </form>
                </Show>
            </div>
        </Section>
    );
};
